import { supabase } from '../../lib/supabase'
import type { RecordState } from '../../components/agent/StatusChip'

// The name of a leave type from the catalog (muster.leave_types), which an
// administrator manages: docs/product/scheduling-model.md's initial types are
// annual, sick, and other approved absence.
export type LeaveType = string

// The leave types a request can name: the open ones, in name order.
export async function getActiveLeaveTypes(): Promise<LeaveType[]> {
  const { data, error } = await supabase.from('leave_types').select('name').eq('active', true).order('name', { ascending: true })
  if (error || !data) return []
  return (data as { name: string }[]).map((row) => row.name)
}

// muster.leave_request_state (supabase/migrations/20260921010000_leave_requests.sql):
// 'pending' is this file's own former 'submitted', renamed to the
// vocabulary a decision actually moves it through.
export type LeaveRequestState = 'pending' | 'approved' | 'returned' | 'denied'

export type LeaveRequest = {
  id: string
  startDate: string
  endDate: string
  fullDay: boolean
  startTime?: string
  endTime?: string
  leaveType: LeaveType
  comment?: string
  state: LeaveRequestState
  submittedAt: string
  // Set once a supervisor decides it: docs/design/journey-map.md's "the
  // supervisor's comments, ... the date and time of the decision."
  decidedAt?: string
  decisionComment?: string
  decidedByName?: string
  // Set only for an approve_modified decision: the subset of
  // startDate..endDate a supervisor actually approved
  // (decisions.modified_dates). Undefined for every other decision,
  // including a plain approve, where the whole requested range is what
  // was granted; a reader that needs "is this specific date actually
  // approved" checks this before falling back to the full range, per
  // isDateApproved below.
  modifiedDates?: string[]
}

export type NewLeaveRequest = Omit<
  LeaveRequest,
  'id' | 'state' | 'submittedAt' | 'decidedAt' | 'decisionComment' | 'decidedByName' | 'modifiedDates'
>

// Whether a specific date within a request's own range was actually
// approved: every date if it was a plain approve (or no decision at
// all), only the supervisor's own modifiedDates if it was an
// approve_modified. AgentCalendarPage.tsx uses this instead of a plain
// startDate/endDate range check wherever "approved" specifically matters
// (the month grid's own leave coloring, the day detail's own approved
// leave entries), since a full-range check alone would keep showing a
// date the supervisor explicitly excluded as if it were still granted.
export function isDateApproved(request: LeaveRequest, date: string): boolean {
  if (request.state !== 'approved') return false
  if (!request.modifiedDates) return true
  return request.modifiedDates.includes(date)
}

// StatusChip.tsx's own RecordState carries no 'pending': every other page
// in this app already calls a not-yet-decided record 'submitted'
// (pendingDraftStore.ts's own schedule submission, for one), so a leave
// request reads the same way rather than introducing a state label
// unique to itself.
export function leaveRequestDisplayState(state: LeaveRequestState): RecordState {
  return state === 'pending' ? 'submitted' : state
}

type LeaveRequestDecisionRow = {
  decision: string
  comment: string | null
  decided_at: string
  decided_by_name: string
  modified_dates: string[] | null
}

type LeaveRequestRow = {
  id: string
  start_date: string
  end_date: string
  full_day: boolean
  start_time: string | null
  end_time: string | null
  comment: string | null
  state: LeaveRequestState
  created_at: string
  leave_types: { name: string } | null
  decisions: LeaveRequestDecisionRow[] | null
}

const LEAVE_REQUEST_COLUMNS =
  'id, start_date, end_date, full_day, start_time, end_time, comment, state, created_at, leave_types(name), decisions(decision, comment, decided_at, decided_by_name, modified_dates)'

function toLeaveRequest(row: LeaveRequestRow): LeaveRequest {
  // decisions is empty until a supervisor decides; a reopened, resubmitted
  // request can gather a second one, so this reads the latest by
  // decided_at rather than assuming PostgREST returns them in order.
  const latest = (row.decisions ?? []).reduce<LeaveRequestDecisionRow | undefined>(
    (latest, current) => (!latest || current.decided_at > latest.decided_at ? current : latest),
    undefined,
  )
  // A decision a supervisor has since reversed no longer stands: the request is
  // pending again, with no decision to show.
  const decision = latest?.decision === 'reverse' ? undefined : latest
  return {
    id: row.id,
    startDate: row.start_date,
    endDate: row.end_date,
    fullDay: row.full_day,
    startTime: row.start_time ?? undefined,
    endTime: row.end_time ?? undefined,
    leaveType: row.leave_types?.name ?? 'Other approved absence',
    comment: row.comment ?? undefined,
    state: row.state,
    submittedAt: row.created_at,
    decidedAt: decision?.decided_at,
    decisionComment: decision?.comment ?? undefined,
    decidedByName: decision?.decided_by_name,
    modifiedDates: decision?.modified_dates ?? undefined,
  }
}

// leave_requests.employee_id is required on every row, but the pages that
// call getLeaveRequests/saveLeaveRequest only ever know the signed-in
// account's own id (session.user.id): this resolves the one in terms of
// the other, the same join employees_read_own itself checks.
async function getMyEmployeeId(userId: string): Promise<string | null> {
  const { data } = await supabase.from('employees').select('id').eq('profile_id', userId).maybeSingle()
  return data?.id ?? null
}

// A list, not a single record: an agent can have more than one leave
// request outstanding at once. Newest first, the same order the request
// history and dashboard both want.
export async function getLeaveRequests(userId: string): Promise<LeaveRequest[]> {
  const employeeId = await getMyEmployeeId(userId)
  if (!employeeId) return []

  const { data, error } = await supabase
    .from('leave_requests')
    .select(LEAVE_REQUEST_COLUMNS)
    .eq('employee_id', employeeId)
    .order('created_at', { ascending: false })
  if (error || !data) return []

  return (data as unknown as LeaveRequestRow[]).map(toLeaveRequest)
}

// The one record the reopen form needs to prefill itself, scoped the same
// way getLeaveRequests is: leave_requests_read_own already refuses
// anything that is not this employee's own, so there is no separate
// ownership check to make here.
export async function getLeaveRequest(userId: string, id: string): Promise<LeaveRequest | null> {
  const employeeId = await getMyEmployeeId(userId)
  if (!employeeId) return null

  const { data, error } = await supabase
    .from('leave_requests')
    .select(LEAVE_REQUEST_COLUMNS)
    .eq('id', id)
    .eq('employee_id', employeeId)
    .maybeSingle()
  if (error || !data) return null

  return toLeaveRequest(data as unknown as LeaveRequestRow)
}

// Returns whether the write actually landed, the same as this file's own
// former sessionStorage version: the caller needs to know before it
// confirms a submission that will not actually be there to reopen.
export async function saveLeaveRequest(userId: string, request: NewLeaveRequest): Promise<boolean> {
  const employeeId = await getMyEmployeeId(userId)
  if (!employeeId) return false

  const { data: leaveType } = await supabase
    .from('leave_types')
    .select('id')
    .eq('name', request.leaveType)
    .maybeSingle()
  if (!leaveType) return false

  const { error } = await supabase.from('leave_requests').insert({
    employee_id: employeeId,
    start_date: request.startDate,
    end_date: request.endDate,
    full_day: request.fullDay,
    start_time: request.startTime ?? null,
    end_time: request.endTime ?? null,
    leave_type_id: leaveType.id,
    comment: request.comment ?? null,
  })
  return !error
}

// Reopens a returned request with edited fields and moves it back to
// pending, through leave_requests_reopen_own
// (20260921010000_leave_requests.sql), the only policy that lets an
// agent update a row they already own rather than insert a new one.
// Returns whether the write actually landed, the same reason
// saveLeaveRequest does: the policy's own using clause silently matches
// zero rows, rather than erroring, if the request is no longer returned.
export async function reopenLeaveRequest(userId: string, id: string, request: NewLeaveRequest): Promise<boolean> {
  const employeeId = await getMyEmployeeId(userId)
  if (!employeeId) return false

  const { data: leaveType } = await supabase
    .from('leave_types')
    .select('id')
    .eq('name', request.leaveType)
    .maybeSingle()
  if (!leaveType) return false

  const { data, error } = await supabase
    .from('leave_requests')
    .update({
      start_date: request.startDate,
      end_date: request.endDate,
      full_day: request.fullDay,
      start_time: request.startTime ?? null,
      end_time: request.endTime ?? null,
      leave_type_id: leaveType.id,
      comment: request.comment ?? null,
      state: 'pending',
      decided_by: null,
      decided_at: null,
      updated_at: new Date().toISOString(),
      updated_by: userId,
    })
    .eq('id', id)
    .eq('employee_id', employeeId)
    .select('id')
  return !error && (data?.length ?? 0) > 0
}

// Cancels a request still awaiting a decision, through
// leave_requests_cancel_own (20260927010000_cancel_pending_leave_request.sql).
// Returns whether a row was actually removed, the same reason
// reopenLeaveRequest does: the policy's using clause silently matches
// zero rows, rather than erroring, if a supervisor decided it first.
export async function cancelLeaveRequest(userId: string, id: string): Promise<boolean> {
  const employeeId = await getMyEmployeeId(userId)
  if (!employeeId) return false

  const { data, error } = await supabase
    .from('leave_requests')
    .delete()
    .eq('id', id)
    .eq('employee_id', employeeId)
    .select('id')
  return !error && (data?.length ?? 0) > 0
}
